/**
 * useScopeTalentAnalytics — Round 74 (A-C-04)
 *
 * Read-only scope-aware talent analytics. Built on top of the Round 73
 * in-scope foundation. Strictly independent from Connect, scheduling,
 * and training hooks.
 *
 * Returns:
 *   - summary: aggregate metrics across the scope
 *   - rows:    per-technician breakdown rows
 *
 * Gated by `canViewInScopeTalent` from useEnterpriseScope.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnterpriseScope } from './useEnterpriseScope';
import type { InScopeScopeType } from './useInScopeTalent';

export interface ScopeTalentSummary {
  technician_count: number;
  total_shop_count: number;
  active_shop_count: number;
  total_jobs_completed: number;
  avg_efficiency: number | null;
  avg_comeback_rate: number | null;
  avg_diagnostic_accuracy: number | null;
  diagnostic_sample_size: number;
  computed_at: string;
}

export interface ScopeTalentBreakdownRow {
  user_id: string;
  display_name: string;
  shop_id: string;
  shop_name: string;
  role: string;
  technician_type_title: string;
  member_since: string;
  jobs_completed: number;
  avg_efficiency: number | null;
  comeback_rate: number | null;
  diagnostic_accuracy: number | null;
  diagnostic_sample_size: number;
}

export interface ScopeTalentFilters {
  shop_ids?: string[];
  role?: string;
  search?: string;
}

interface UseScopeTalentAnalyticsArgs {
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
  filters?: ScopeTalentFilters;
}

export function useScopeTalentAnalytics({
  scopeType,
  scopeId,
  filters,
}: UseScopeTalentAnalyticsArgs) {
  const { canViewInScopeTalent } = useEnterpriseScope();
  const enabled = canViewInScopeTalent && !!scopeType && !!scopeId;

  const summaryQuery = useQuery({
    queryKey: ['scope-talent-summary', scopeType, scopeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_talent_summary', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
      });
      if (error) throw error;
      const row = (data ?? [])[0] as ScopeTalentSummary | undefined;
      return row ?? null;
    },
    enabled,
  });

  // Server-side filters are applied via p_filters jsonb. Only non-empty fields
  // are forwarded so missing keys mean "no filter".
  const cleanFilters = filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : !!v,
        ),
      )
    : {};

  const rowsQuery = useQuery({
    queryKey: ['scope-talent-breakdown', scopeType, scopeId, cleanFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_talent_breakdown', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
        p_filters: cleanFilters as any,
      });
      if (error) throw error;
      return (data ?? []) as ScopeTalentBreakdownRow[];
    },
    enabled,
  });

  return {
    summary: summaryQuery.data ?? null,
    rows: rowsQuery.data ?? [],
    isLoading: summaryQuery.isLoading || rowsQuery.isLoading,
    error: (summaryQuery.error ?? rowsQuery.error) as Error | null,
  };
}
